import { appEnv } from "@/constants/app-env";
import { env } from "@/constants/env";
import { supabase } from "@/lib/supabase";

const getUrl = (contextUrl: string): string => {
  const baseUrl = env.API_BASE;

  if (appEnv.isDevelopment) {
    // NOTE: Use Proxy to avoid CORS in Development environment
    try {
      const url = new URL(contextUrl);
      const pathname = url.pathname;
      const search = url.search;

      return `${pathname}${search}`;
    } catch {
      return contextUrl;
    }
  }
  const url = new URL(contextUrl);
  const pathname = url.pathname;
  const search = url.search;
  // TODO: Find a workaround for https://github.com/orval-labs/orval/issues/1185
  // It shows warning when orval generates the client.

  const requestUrl = new URL(`${baseUrl}${pathname}${search}`);

  return requestUrl.toString();
};

const getHeaders = async (headers?: HeadersInit): Promise<HeadersInit> => {
  const accessToken = (await supabase.auth.getSession()).data.session?.access_token;

  let _headers = { ...headers };
  if (accessToken !== undefined) {
    _headers = {
      ..._headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return _headers;
};

export const apiClient = async <T>(url: string, options: RequestInit): Promise<T> => {
  const requestUrl = getUrl(url);
  const requestHeaders = await getHeaders(options.headers);

  const requestInit: RequestInit = {
    ...options,
    headers: requestHeaders,
  };

  const request = new Request(requestUrl, requestInit);
  const response = await fetch(request);

  if (response.status === 401) {
    throw new KnownAuthError("Unauthorized", window.location.href);
  }

  if (response.status >= 400 && response.status < 500) {
    const errorData = await response.json();
    throw new KnownError(errorData.message ?? response.statusText, response.status);
  }

  if (response.status >= 500) {
    throw new Error(response.statusText);
  }

  if (response.status === 204 || response.status === 201 || options.method === "PATCH") {
    return { status: response.status, data: null, headers: response.headers } as T;
  }

  const data = await response.json();

  return { status: response.status, data, headers: response.headers } as T;
};

export class KnownError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "KnownError";
    this.status = status;
  }

  static is(error: unknown): error is KnownError {
    return error instanceof KnownError;
  }
}

export class KnownAuthError extends KnownError {
  redirectUrl?: string;

  constructor(message: string, redirectUrl?: string) {
    super(message, 401);
    this.name = "KnownAuthError";
    this.redirectUrl = redirectUrl;
  }

  static is(error: unknown): error is KnownAuthError {
    return error instanceof KnownAuthError;
  }
}

apiClient.post = async <T>(path: string, { body }: { headers?: HeadersInit; body: BodyInit }) => {
  const url = `${env.API_BASE}${path}`;
  const response = await apiClient<T>(url, {
    method: "POST",
    body,
    credentials: "include",
  });

  return response;
};
